import type { ActivityEntry } from "../api/client";
import { prettyFieldLabel } from "../utils/labels";

// ---------------------------------------------------------------------------
// Action description helpers
// ---------------------------------------------------------------------------

function getInitials(email?: string): string {
  if (!email) return "?";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function describeAction(entry: ActivityEntry): string {
  const detail = entry.detail ?? {};

  switch (entry.action) {
    // Deal lifecycle
    case "deal_created":
      return `created this deal`;
    case "deal_updated":
      return `updated deal settings`;

    // Documents
    case "document_uploaded":
      return `uploaded ${detail.filename ?? "a document"}`;
    case "document_parsed":
      return `parsed ${detail.filename ?? "a document"}`;
    case "document_enhanced":
      return `enhanced ${detail.filename ?? "a document"} with AI`;
    case "document_deleted":
      return `deleted ${detail.filename ?? "a document"}`;
    case "document_reclassified":
      return `reclassified ${detail.filename ?? "a document"} as ${detail.file_type ?? "unknown"}`;

    // Scenarios
    case "scenario_created":
      return `created scenario "${detail.name ?? "Untitled"}"`;
    case "scenario_updated":
      return `updated scenario "${detail.name ?? "Untitled"}"`;
    case "scenario_deleted":
      return `deleted scenario "${detail.name ?? "Untitled"}"`;
    case "scenario_run":
      return `ran projection for "${detail.name ?? "scenario"}"`;

    // Tenants
    case "tenant_added":
      return `added tenant "${detail.tenant_name ?? "Unknown"}"`;
    case "tenant_updated":
      return `updated tenant "${detail.tenant_name ?? "Unknown"}"`;
    case "tenant_deleted":
      return `deleted a tenant`;

    // Expenses
    case "expenses_updated":
      return `updated expense overrides`;

    // Access
    case "access_granted":
      return `granted ${detail.role ?? "viewer"} access to ${detail.email ?? "a user"}`;
    case "access_revoked":
      return `revoked access for ${detail.email ?? "a user"}`;

    // Comments
    case "comment_created":
    case "create_comment":
      return `added a comment${detail.page ? ` on ${detail.page}` : ""}`;
    case "comment_resolved":
    case "resolve_comment":
      return `resolved a comment`;
    case "unresolve_comment":
      return `reopened a comment`;

    // Assumptions
    case "assumptions_updated":
    case "update_assumptions":
      return `updated assumptions`;

    // Projection
    case "projection_run":
      return `ran a projection`;

    // Actual action names from routes
    case "upload_document":
      return `uploaded ${detail.filename ?? "a document"}`;
    case "parse_document":
      return `parsed a document`;
    case "enhance_document":
      return `enhanced a document with AI${detail.before_score != null ? ` (${detail.before_score} → ${detail.after_score})` : ""}`;
    case "create_scenario":
      return `created scenario "${detail.name ?? "Untitled"}"`;
    case "grant_access":
      return `granted access to ${detail.user_email ?? "a user"}`;
    case "revoke_access":
      return `revoked access for ${detail.user_email ?? "a user"}`;
    case "export_argus":
      return `exported Excel file`;
    case "export_pdf":
      return `downloaded PDF summary`;
    case "update_deal":
      return `updated deal settings`;
    case "update_expense_pool":
      return `updated expense pool "${detail.pool_name ?? ""}"`;

    default:
      return prettyFieldLabel(entry.action);
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Action type color mapping
// ---------------------------------------------------------------------------

function getActionColor(action: string): { bg: string; text: string } {
  if (action.startsWith("document_")) return { bg: "bg-blue-100", text: "text-blue-700" };
  if (action.startsWith("scenario_") || action === "projection_run") return { bg: "bg-purple-100", text: "text-purple-700" };
  if (action.startsWith("tenant_")) return { bg: "bg-green-100", text: "text-green-700" };
  if (action.startsWith("access_")) return { bg: "bg-honey-100", text: "text-honey-700" };
  if (action.startsWith("comment_")) return { bg: "bg-orange-100", text: "text-orange-700" };
  if (action.startsWith("deal_")) return { bg: "bg-forest-100", text: "text-forest-700" };
  return { bg: "bg-grove-200", text: "text-bark-600" };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ActivityFeedProps {
  entries: ActivityEntry[];
  compact?: boolean;
}

export default function ActivityFeed({ entries, compact = false }: ActivityFeedProps) {
  if (entries.length === 0) {
    return (
      <div className="px-5 py-8 text-center">
        <p className="text-sm text-bark-400">No activity to display.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-grove-100">
      {entries.map((entry) => {
        const initials = getInitials(entry.user_email);
        const colors = getActionColor(entry.action);
        return (
          <div
            key={entry.id}
            className={`flex items-start gap-3 hover:bg-grove-50/50 transition-colors ${
              compact ? "px-4 py-2.5" : "px-5 py-3"
            }`}
          >
            {/* Avatar circle */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${colors.bg} ${colors.text}`}
              title={entry.user_email ?? entry.user_id}
            >
              {initials}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className={`leading-snug ${compact ? "text-xs" : "text-sm"} text-bark-700`}>
                <span className="font-medium text-bark-800">
                  {entry.user_email?.split("@")[0] ?? "User"}
                </span>{" "}
                {describeAction(entry)}
              </p>
              <p className={`text-bark-400 mt-0.5 ${compact ? "text-[10px]" : "text-[11px]"}`}>
                {timeAgo(entry.created_at)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
